const axios = require('axios');

class HTTPResponseException {
  constructor(body, response, originalError) {
    this.name = this.constructor.name
    this.response = response;
    this.originalError = originalError;
    this.status = response.status;
    this.requestId = response.headers['x-request-id'];

    try {
      if(body instanceof Object && body.hasOwnProperty('error')) {
        this.json = body;
      } else {
        this.json = JSON.parse(body);
      }
      this.description = this.json.error.description;
      this.detail = this.json.error.details;

      this.message = this.description;
    } catch (e) {
      this.message = body;
    }
    this.name = this.constructor.name;
  }
}

class HTTPClientError extends HTTPResponseException { }
class HTTPBadRequest extends HTTPClientError { }
class HTTPUnauthorized extends HTTPClientError { }
class HTTPForbidden extends HTTPClientError { }
class HTTPNotFound extends HTTPClientError { }
class HTTPNotATeapot extends HTTPClientError { }

class HTTPServerError extends HTTPResponseException { }
class HTTPInternalServerError extends HTTPServerError { }
class HTTPBadGateway extends HTTPServerError { }
class HTTPServiceUnavailable extends HTTPServerError { }

const errorStatusMap = {
  400: HTTPBadRequest,
  401: HTTPUnauthorized,
  403: HTTPForbidden,
  404: HTTPNotFound,
  418: HTTPNotATeapot,
  500: HTTPInternalServerError,
  502: HTTPBadGateway,
  503: HTTPServiceUnavailable
}

class HTTPClient {
  constructor({ clientId = undefined, clientSecret = undefined, baseURL = undefined, token = undefined, useSessionAuth = undefined, csrfToken = undefined, crossDomainCredentials = undefined } = {}) {
    this.singularizer = HTTPClient._quakeSingularizer ||= {};

    this.baseURL = baseURL || HTTPClient.config.baseURL || (typeof window === 'undefined' ? null : window.location.origin);

    this.useSessionAuth = 'undefined' === typeof useSessionAuth ? HTTPClient.config.useSessionAuth : useSessionAuth;
    this.crossDomainCredentials = crossDomainCredentials || HTTPClient.config.crossDomainCredentials;
    this.csrfToken = csrfToken || HTTPClient.config.csrfToken;

    this.clientId = clientId || HTTPClient.config.clientId;
    this.clientSecret = clientSecret || HTTPClient.config.clientSecret;
    this.token = token || HTTPClient.config.token;

    this.client = HTTPClient.config.client || this._createClient();

    this.get = this.get.bind(this);
    this.post = this.client.post;
    this.put = this.client.put;
    this.patch = this.client.patch;
    this.delete = this.client.delete;
  }

  get(path) {
    return this.getThroughSingularizer(path);
  }

  getThroughSingularizer(path) {
    const key = `get:${path}`;

    if (this.singularizer[key]) {
      return this.singularizer[key];
    } else {
      this.singularizer[key] = this.client.get(path).then(response => {
        delete this.singularizer[key];
        return response;
      }).catch(
        error => {
          delete this.singularizer[key];
          throw error;
        }
      );

      return this.singularizer[key];
    }
  }

  async _handleErrorResponse(error) {
    const errorClass = errorStatusMap[error.response?.status];
    if(!errorClass) { throw error }
    throw new errorClass(error.response.data, error.response, error);
  }

  async _handleResponse(response) { return(response.data) }

  async getToken({ clientId, clientSecret, baseURL }) {
    console.debug(`Logging ${clientId} into ${baseURL}`);
    let authResponse;
    await axios.post('/api/oauth/token',
      {},
      {
        params: {
          grant_type: 'client_credentials'
        },
        auth: { username: clientId, password: clientSecret },
        baseURL
      }).then(res => {
        const expiresAt = new Date().getTime() + ((res.data.expires_in - 1) * 1000);
        console.debug(`Successfully obtained new token for ${clientId} expiring at ${new Date(expiresAt)}`);
        authResponse = { accessToken: res.data.access_token, expiresIn: res.data.expires_in, expiresAt };
      }
      )
    return (authResponse);
  }

  async _clientCredsTokenAuthRequestInterceptor(config) {
    if (this.token && this.token.expiresAt && this.token.expiresAt < new Date().getTime()) {
      console.debug('Token has expired; forgetting token.');
      this.token = undefined;
    }
    if (!this.token) {
      config.authAttempted = true;
      this.token = await this.getToken({ clientId: this.clientId, clientSecret: this.clientSecret, baseURL: config.baseURL });
    }
    return ({
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${this.token.accessToken}`
      }
    })
  }

  async _clientCredsTokenAuthResponseInterceptor(error) {
    const { config, message, response, ...rest } = error;
    if(response?.status !== 401) { return(Promise.reject(error)) } // no-op

    if(config.auth?.username === this.clientId && config.auth?.password === this.clientSecret) {
      console.warn('Failed to authenticate');
      return(Promise.reject(error));
    }

    this.token = undefined;
    console.debug('Authentication issue... token may have expired... retrying');
    return new Promise(resolve => resolve(this.client(config)));
  }

  async _sessionAuthRequestInterceptor(config) {
    return ({
      ...config,
      headers: {
        ...config.headers,
        'X-CSRF-Token': this.csrfToken
      }
    })
  }

  async _sessionAuthResponseInterceptor(error) {
    const { config, message, response, ...rest } = error;

    // no-op for now.
    // TODO: handle logged out user
    return(Promise.reject(error));
  }

  _createClient() {
    const client = axios.create({ baseURL: this.baseURL, withCredentials: this.crossDomainCredentials });
    if(this.useSessionAuth) {
      client.interceptors.request.use(this._sessionAuthRequestInterceptor.bind(this));
      client.interceptors.response.use(undefined, this._sessionAuthResponseInterceptor.bind(this));
    } else {
      client.interceptors.request.use(this._clientCredsTokenAuthRequestInterceptor.bind(this));
      client.interceptors.response.use(undefined, this._clientCredsTokenAuthResponseInterceptor.bind(this));
    }

    client.interceptors.response.use(this._handleResponse.bind(this), this._handleErrorResponse.bind(this));
    return(client);
  }

  static config = {};
}

HTTPClient.HTTPResponseException = HTTPResponseException;

HTTPClient.HTTPClientError = HTTPClientError;
HTTPClient.HTTPBadRequest = HTTPBadRequest;
HTTPClient.HTTPUnauthorized = HTTPUnauthorized;
HTTPClient.HTTPForbidden = HTTPForbidden;
HTTPClient.HTTPNotFound = HTTPNotFound;
HTTPClient.HTTPNotATeapot = HTTPNotATeapot;

HTTPClient.HTTPServerError = HTTPServerError;
HTTPClient.HTTPInternalServerError = HTTPInternalServerError;
HTTPClient.HTTPBadGateway = HTTPBadGateway;
HTTPClient.HTTPServiceUnavailable = HTTPServiceUnavailable;

module.exports = HTTPClient;
